import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster, toast } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import CaseDetail from "@/pages/CaseDetail";
import Pricing from "@/pages/Pricing";
import Account from "@/pages/Account";
import Brain from "@/pages/Brain";
import BillingSuccess from "@/pages/BillingSuccess";
import PropertyCheck from "@/pages/PropertyCheck";
import Founder from "@/pages/Founder";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Disclaimer from "@/pages/Disclaimer";
import Refunds from "@/pages/Refunds";
import Support from "@/pages/Support";
import Guide from "@/pages/Guide";
import QAIndex from "@/pages/QAIndex";
import QASnippet from "@/pages/QASnippet";
import NotFound from "@/pages/NotFound";
import VerifyEmail from "@/pages/VerifyEmail";
import VerifyHash from "@/pages/VerifyHash";
import PublicComparison from "@/pages/PublicComparison";
import Features from "@/pages/Features";
import AdminInsights from "@/pages/AdminInsights";
import BrandPhoneCapture from "@/pages/BrandPhoneCapture";
import PublicVoiceNote from "@/pages/PublicVoiceNote";
import SharedFinding from "@/pages/SharedFinding";
import QAHubEntry from "@/pages/QAHubEntry";
import AllCasesCalendar from "@/pages/AllCasesCalendar";
import MoveOutMode from "@/pages/MoveOutMode";
import CheckADeduction from "@/pages/CheckADeduction";
import EmbedCheckADeduction from "@/pages/EmbedCheckADeduction";
import Trust from "@/pages/Trust";
import FloatingMicButton from "@/components/FloatingMicButton";

function Protected({ children }) {
 const { user, loading } = useAuth();
 if (loading) return <div className="min-h-screen flex items-center justify-center text-[#6B7280]">Loading…</div>;
 if (!user) return <Navigate to="/login" replace />;
 return children;
}

/**
 * Listens for 402-quota events emitted by the api interceptor and shows a
 * single toast with a "Get a Boost" CTA. Keeps quota error UX consistent
 * across every AI endpoint without needing to change each component.
 */
function QuotaErrorHandler() {
 const navigate = useNavigate();
 React.useEffect(() => {
 const onQuota = (e) => {
 const { message, boost } = e.detail || {};
 toast.error(message || "Out of queries this month.", {
 duration: 8000,
 action: boost
 ? { label: "Get a Boost", onClick: () => navigate("/account?boost=1") }
 : { label: "See plans", onClick: () => navigate("/pricing") },
 });
 };
 window.addEventListener("tt:quota-exceeded", onQuota);
 return () => window.removeEventListener("tt:quota-exceeded", onQuota);
 }, [navigate]);
 return null;
}

export default function App() {
 return (
 <BrowserRouter>
 <ScrollToTop />
 <AppErrorBoundary>
 <AuthProvider>
 <Toaster
 position="bottom-right"
 duration={3500}
 toastOptions={{
 classNames: {
 toast: "!bg-white !border !border-[#E5E4DE] !text-[#1A1A1A] !rounded-none !shadow-lg !font-serif",
 title: "!font-serif !text-[15px] !text-[#0D2B52]",
 description: "!text-[13px] !text-[#6B7280]",
 success: "!border-l-4 !border-l-[#1B6C62]",
 error: "!border-l-4 !border-l-[#B35D4C]",
 info: "!border-l-4 !border-l-[#0D2B52]",
 },
 }}
 />
 <QuotaErrorHandler />
 <Routes>
 <Route path="/" element={<Landing />} />
 <Route path="/login" element={<AuthPage mode="login" />} />
 <Route path="/register" element={<AuthPage mode="register" />} />
 <Route path="/pricing" element={<Pricing />} />
 <Route path="/ask" element={<Brain />} />
 <Route path="/check" element={<PropertyCheck />} />
            <Route path="/check-a-deduction" element={<CheckADeduction />} />
            <Route path="/embed/check-a-deduction" element={<EmbedCheckADeduction />} />
            <Route path="/trust" element={<Trust />} />
 <Route path="/founder" element={<Founder />} />
 <Route path="/privacy" element={<Privacy />} />
 <Route path="/terms" element={<Terms />} />
 <Route path="/disclaimer" element={<Disclaimer />} />
 <Route path="/refunds" element={<Refunds />} />
 <Route path="/support" element={<Support />} />
 <Route path="/guide" element={<Guide />} />
 <Route path="/q" element={<QAIndex />} />
 <Route path="/q/:state/:slug" element={<QAHubEntry />} />
 <Route path="/q/:slug" element={<QASnippet />} />
 <Route path="/verify/:hash" element={<VerifyHash />} />
 <Route path="/c/:token" element={<PublicComparison />} />
 <Route path="/v/:token" element={<PublicVoiceNote />} />
 <Route path="/share/finding/:token" element={<SharedFinding />} />
 <Route path="/features" element={<Features />} />
 <Route path="/admin/insights" element={<Protected><AdminInsights /></Protected>} />
 <Route path="/brand/phone" element={<BrandPhoneCapture />} />
 <Route path="/billing/success" element={<BillingSuccess />} />
 <Route path="/verify-email" element={<VerifyEmail />} />
 <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
 <Route path="/calendar" element={<Protected><AllCasesCalendar /></Protected>} />
 <Route path="/cases/:id/move-out" element={<Protected><MoveOutMode /></Protected>} />
 <Route path="/cases/new" element={<Navigate to="/dashboard?new=1" replace />} />
 <Route path="/cases/:id" element={<Protected><CaseDetail /></Protected>} />
 <Route path="/account" element={<Protected><Account /></Protected>} />
 <Route path="*" element={<NotFound />} />
 </Routes>
 <FloatingMicButton />
 </AuthProvider>
 </AppErrorBoundary>
 </BrowserRouter>
 );
}
